/**
 * AlarmSettingsModal Component
 *
 * Focused modal for managing alarm rules (CRUD) during live trading.
 * Two-view pattern: list view (rules + reorder/toggle/delete) and
 * form view (add/edit with full field set).
 *
 * Loads alarm rules from LiveTradingSettingsService on open, manages
 * local state, and persists via updateAlarmConfig() on Save.
 *
 * Auto-dismiss duration setting lives in FeaturesDropdown (not here).
 *
 * @see FeaturesDropdown.tsx — mounts this modal via Alarms accordion item
 * @see LiveTradingSettingsService.updateAlarmConfig — persistence
 * @see types/shared/AlarmRule.ts — alarm rule discriminated union
 * @see constants/alarmTone.ts — tone display metadata
 */

// 1. External dependencies
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { ArrowLeft, ChevronUp, ChevronDown, Pencil, Trash2, Volume2 } from 'lucide-react';

// 2. Internal absolute imports
import {
  Modal, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Card, CardHeader, CardBody,
  Button, Input, Select, SelectItem, Switch,
  Autocomplete, AutocompleteItem,
  useDisclosure,
} from '@enfomentis/ui';
import { CATEGORY_COLORS } from '@/constants/colors';
import { ALARM_TONE_IDS, ALARM_TONE_DISPLAY_MAP } from '@/constants/alarmTone';
import { TIMEZONES } from '@/constants/timezone';
import { generateUUID } from '@/utils';
import { playAlarmTone } from '@/utils/alarmSound';
import { liveTradingSettingsService } from '@/services/LiveTradingSettingsService';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { UnsavedChangesDialog } from '@/components/shared/UnsavedChangesDialog';

// 3. Type imports
import type { AlarmRule, AlarmRuleType, AlarmColor, AlarmToneId } from '@/types/shared/AlarmRule';

// ===== CONSTANTS =====

/** Display metadata for the 4 alarm rule types */
const RULE_TYPE_OPTIONS: { value: AlarmRuleType; label: string; description: string }[] = [
  { value: 'candle-close-countdown', label: 'Candle Close Countdown', description: 'Alert before a candle closes' },
  { value: 'fixed-interval', label: 'Fixed Interval', description: 'Alert every N minutes' },
  { value: 'clock-mark', label: 'Clock Mark', description: 'Alert at specific minutes each hour' },
  { value: 'specific-time', label: 'Specific Time', description: 'Alert once at a specific time' },
];

/** Empty form state for adding a new alarm rule */
const EMPTY_FORM = {
  type: 'fixed-interval' as AlarmRuleType,
  message: '',
  color: 'primary' as AlarmColor,
  soundId: 'chime' as AlarmToneId | null,
  timezoneOverride: '',
  candleIntervalMinutes: '60',
  alertBeforeMinutes: '1',
  intervalMinutes: '15',
  minuteMarks: '0, 15, 30, 45',
  time: '09:30',
};

/** Standard candle timeframes for the candle-close-countdown rule type Select */
const CANDLE_TIMEFRAMES: { value: number; label: string }[] = [
  { value: 1, label: '1m' },
  { value: 5, label: '5m' },
  { value: 15, label: '15m' },
  { value: 30, label: '30m' },
  { value: 60, label: '1H' },
  { value: 240, label: '4H' },
  { value: 1440, label: 'Daily' },
];

/** Explicit Tailwind bg-class map for alarm rule color dots (avoids dynamic class construction) */
const ALARM_COLOR_BG_CLASS: Record<AlarmColor, string> = {
  default: 'bg-default-400',
  primary: 'bg-primary',
  secondary: 'bg-secondary',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

// ===== TYPES =====

export interface AlarmSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ===== HELPERS =====

/** Format a rule type + key config into a short summary string for the list */
function formatRuleSummary(rule: AlarmRule): string {
  switch (rule.type) {
    case 'candle-close-countdown':
      return `${rule.alertBeforeMinutes}min before ${rule.candleIntervalMinutes}min candle`;
    case 'fixed-interval':
      return `Every ${rule.intervalMinutes} min`;
    case 'clock-mark':
      return `At :${rule.minuteMarks.map((m) => String(m).padStart(2, '0')).join(', :')}`;
    case 'specific-time':
      return `At ${rule.time}`;
  }
}

/**
 * Normalize order values to sequential 0, 1, 2...
 * Defensive against drifted/duplicate orders from sync corruption.
 */
function normalizeOrders(rules: AlarmRule[]): AlarmRule[] {
  const sorted = [...rules].sort((a, b) => a.order - b.order);
  return sorted.map((rule, idx) => (rule.order === idx ? rule : { ...rule, order: idx }));
}

/** Validate HH:MM format — returns true if valid */
function isValidTimeFormat(value: string): boolean {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return false;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
}

/** Parse comma-separated minute marks — returns array of numbers or null if invalid */
function parseMinuteMarks(value: string): number[] | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  const marks: number[] = [];
  for (const part of parts) {
    const n = Number(part);
    if (!Number.isInteger(n) || n < 0 || n > 59) return null;
    marks.push(n);
  }
  return marks;
}

/** Find the nearest standard candle timeframe for a non-standard interval value */
function findNearestTimeframe(minutes: number): number {
  return CANDLE_TIMEFRAMES.reduce((nearest, tf) =>
    Math.abs(tf.value - minutes) < Math.abs(nearest.value - minutes) ? tf : nearest,
  ).value;
}

// ===== COMPONENT =====

export const AlarmSettingsModal: React.FC<AlarmSettingsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const isDesktop = useIsDesktop();

  // ---------------------------------------------------------------------------
  // Settings state (loaded from service on open)
  // ---------------------------------------------------------------------------

  const [isLoading, setIsLoading] = useState(false);
  const [alarmRules, setAlarmRules] = useState<AlarmRule[]>([]);

  // ---------------------------------------------------------------------------
  // Alarm rule form state
  // ---------------------------------------------------------------------------

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | undefined>(undefined);
  const [formType, setFormType] = useState<AlarmRuleType>(EMPTY_FORM.type);
  const [formMessage, setFormMessage] = useState(EMPTY_FORM.message);
  const [formColor, setFormColor] = useState<AlarmColor>(EMPTY_FORM.color);
  const [formSoundId, setFormSoundId] = useState<AlarmToneId | null>(EMPTY_FORM.soundId);
  const [formTimezoneOverride, setFormTimezoneOverride] = useState(EMPTY_FORM.timezoneOverride);
  const [formCandleInterval, setFormCandleInterval] = useState(EMPTY_FORM.candleIntervalMinutes);
  const [formAlertBefore, setFormAlertBefore] = useState(EMPTY_FORM.alertBeforeMinutes);
  const [formIntervalMinutes, setFormIntervalMinutes] = useState(EMPTY_FORM.intervalMinutes);
  const [formMinuteMarks, setFormMinuteMarks] = useState(EMPTY_FORM.minuteMarks);
  const [formTime, setFormTime] = useState(EMPTY_FORM.time);

  // ---------------------------------------------------------------------------
  // Form snapshot for dirty detection
  // ---------------------------------------------------------------------------

  interface FormSnapshot {
    type: AlarmRuleType;
    message: string;
    color: AlarmColor;
    soundId: AlarmToneId | null;
    timezoneOverride: string;
    candleInterval: string;
    alertBefore: string;
    intervalMinutes: string;
    minuteMarks: string;
    time: string;
  }

  const formInitialRef = useRef<FormSnapshot>({
    type: EMPTY_FORM.type,
    message: EMPTY_FORM.message,
    color: EMPTY_FORM.color,
    soundId: EMPTY_FORM.soundId,
    timezoneOverride: EMPTY_FORM.timezoneOverride,
    candleInterval: EMPTY_FORM.candleIntervalMinutes,
    alertBefore: EMPTY_FORM.alertBeforeMinutes,
    intervalMinutes: EMPTY_FORM.intervalMinutes,
    minuteMarks: EMPTY_FORM.minuteMarks,
    time: EMPTY_FORM.time,
  });

  const isFormDirty = useMemo(() => {
    if (!isFormOpen) return false;
    const s = formInitialRef.current;
    return (
      formType !== s.type ||
      formMessage !== s.message ||
      formColor !== s.color ||
      formSoundId !== s.soundId ||
      formTimezoneOverride !== s.timezoneOverride ||
      formCandleInterval !== s.candleInterval ||
      formAlertBefore !== s.alertBefore ||
      formIntervalMinutes !== s.intervalMinutes ||
      formMinuteMarks !== s.minuteMarks ||
      formTime !== s.time
    );
  }, [isFormOpen, formType, formMessage, formColor, formSoundId, formTimezoneOverride, formCandleInterval, formAlertBefore, formIntervalMinutes, formMinuteMarks, formTime]);

  // ---------------------------------------------------------------------------
  // Validation error state
  // ---------------------------------------------------------------------------

  const [messageError, setMessageError] = useState<string | null>(null);
  const [typeFieldError, setTypeFieldError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Save state
  // ---------------------------------------------------------------------------

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Reset on open (prev-ref pattern)
  // ---------------------------------------------------------------------------

  const prevIsOpenRef = useRef(isOpen);

  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (!wasOpen && isOpen) {
      // Reset form state
      setIsFormOpen(false);
      setEditingRuleId(undefined);
      setFormType(EMPTY_FORM.type);
      setFormMessage(EMPTY_FORM.message);
      setFormColor(EMPTY_FORM.color);
      setFormSoundId(EMPTY_FORM.soundId);
      setFormTimezoneOverride(EMPTY_FORM.timezoneOverride);
      setFormCandleInterval(EMPTY_FORM.candleIntervalMinutes);
      setFormAlertBefore(EMPTY_FORM.alertBeforeMinutes);
      setFormIntervalMinutes(EMPTY_FORM.intervalMinutes);
      setFormMinuteMarks(EMPTY_FORM.minuteMarks);
      setFormTime(EMPTY_FORM.time);
      setMessageError(null);
      setTypeFieldError(null);
      setIsSaving(false);
      setSaveError(null);

      // Load settings from service
      setIsLoading(true);
      liveTradingSettingsService.getOrCreateDefaultSettings()
        .then((settings) => {
          const rules = settings.alarmRules ?? [];
          setAlarmRules(normalizeOrders(rules));
        })
        .catch(() => {
          setAlarmRules([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen]);

  // ---------------------------------------------------------------------------
  // Handlers — alarm rule form
  // ---------------------------------------------------------------------------

  const resetFormFields = useCallback((): void => {
    setFormType(EMPTY_FORM.type);
    setFormMessage(EMPTY_FORM.message);
    setFormColor(EMPTY_FORM.color);
    setFormSoundId(EMPTY_FORM.soundId);
    setFormTimezoneOverride(EMPTY_FORM.timezoneOverride);
    setFormCandleInterval(EMPTY_FORM.candleIntervalMinutes);
    setFormAlertBefore(EMPTY_FORM.alertBeforeMinutes);
    setFormIntervalMinutes(EMPTY_FORM.intervalMinutes);
    setFormMinuteMarks(EMPTY_FORM.minuteMarks);
    setFormTime(EMPTY_FORM.time);
    setMessageError(null);
    setTypeFieldError(null);
  }, []);

  const openAddForm = useCallback((): void => {
    resetFormFields();
    setEditingRuleId(undefined);
    formInitialRef.current = {
      type: EMPTY_FORM.type,
      message: EMPTY_FORM.message,
      color: EMPTY_FORM.color,
      soundId: EMPTY_FORM.soundId,
      timezoneOverride: EMPTY_FORM.timezoneOverride,
      candleInterval: EMPTY_FORM.candleIntervalMinutes,
      alertBefore: EMPTY_FORM.alertBeforeMinutes,
      intervalMinutes: EMPTY_FORM.intervalMinutes,
      minuteMarks: EMPTY_FORM.minuteMarks,
      time: EMPTY_FORM.time,
    };
    setIsFormOpen(true);
  }, [resetFormFields]);

  const openEditForm = useCallback((rule: AlarmRule): void => {
    // Reset ALL fields to defaults first — prevents stale non-active type fields
    // from causing dirty false positives when comparing against snapshot
    resetFormFields();

    setEditingRuleId(rule.id);
    setFormType(rule.type);
    setFormMessage(rule.message);
    setFormColor(rule.color);
    setFormSoundId(rule.soundId);
    setFormTimezoneOverride(rule.timezoneOverride ?? '');

    // Populate type-specific fields
    switch (rule.type) {
      case 'candle-close-countdown':
        setFormCandleInterval(String(rule.candleIntervalMinutes));
        setFormAlertBefore(String(rule.alertBeforeMinutes));
        break;
      case 'fixed-interval':
        setFormIntervalMinutes(String(rule.intervalMinutes));
        break;
      case 'clock-mark':
        setFormMinuteMarks(rule.minuteMarks.join(', '));
        break;
      case 'specific-time':
        setFormTime(rule.time);
        break;
    }

    // Snapshot the form state for dirty detection (after setters above, ref is sync)
    formInitialRef.current = {
      type: rule.type,
      message: rule.message,
      color: rule.color,
      soundId: rule.soundId,
      timezoneOverride: rule.timezoneOverride ?? '',
      candleInterval: rule.type === 'candle-close-countdown' ? String(rule.candleIntervalMinutes) : EMPTY_FORM.candleIntervalMinutes,
      alertBefore: rule.type === 'candle-close-countdown' ? String(rule.alertBeforeMinutes) : EMPTY_FORM.alertBeforeMinutes,
      intervalMinutes: rule.type === 'fixed-interval' ? String(rule.intervalMinutes) : EMPTY_FORM.intervalMinutes,
      minuteMarks: rule.type === 'clock-mark' ? rule.minuteMarks.join(', ') : EMPTY_FORM.minuteMarks,
      time: rule.type === 'specific-time' ? rule.time : EMPTY_FORM.time,
    };

    setIsFormOpen(true);
  }, [resetFormFields]);

  /** Unguarded close — used after successful save (no dirty check needed) */
  const closeFormSilent = useCallback((): void => {
    setIsFormOpen(false);
    setEditingRuleId(undefined);
    setMessageError(null);
    setTypeFieldError(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Unsaved changes dialog (replaces window.confirm for dirty-form guards)
  // ---------------------------------------------------------------------------

  const discardDialog = useDisclosure();
  const pendingDiscardActionRef = useRef<(() => void) | null>(null);

  const handleDiscard = useCallback((): void => {
    pendingDiscardActionRef.current?.();
    pendingDiscardActionRef.current = null;
    discardDialog.onClose();
  }, [discardDialog]);

  const handleDiscardCancel = useCallback((): void => {
    pendingDiscardActionRef.current = null;
    discardDialog.onClose();
  }, [discardDialog]);

  /** Guarded close — used for user-initiated cancel/back */
  const closeForm = useCallback((): void => {
    if (isFormDirty) {
      pendingDiscardActionRef.current = closeFormSilent;
      discardDialog.onOpen();
      return;
    }
    closeFormSilent();
  }, [isFormDirty, closeFormSilent, discardDialog]);

  const handleSaveRule = useCallback((): void => {
    // Validate message
    if (!formMessage.trim()) {
      setMessageError('Message is required.');
      return;
    }
    setMessageError(null);

    // Common fields shared by all rule types
    const base = {
      message: formMessage.trim(),
      color: formColor,
      soundId: formSoundId,
      ...(formTimezoneOverride ? { timezoneOverride: formTimezoneOverride } : {}),
    };

    // Build the complete rule per type (avoids Omit on discriminated union)
    let rule: AlarmRule;
    const id = editingRuleId ?? generateUUID();
    const order = editingRuleId !== undefined
      ? (alarmRules.find((r) => r.id === editingRuleId)?.order ?? 0)
      : alarmRules.reduce((max, r) => Math.max(max, r.order), -1) + 1;
    const enabled = editingRuleId !== undefined
      ? (alarmRules.find((r) => r.id === editingRuleId)?.enabled ?? true)
      : true;

    switch (formType) {
      case 'candle-close-countdown': {
        let interval = parseInt(formCandleInterval, 10);
        const before = parseInt(formAlertBefore, 10);
        if (!Number.isFinite(interval) || interval <= 0) {
          setTypeFieldError('Candle interval must be a positive number.');
          return;
        }
        // Coerce non-standard intervals to nearest standard timeframe on save
        if (!CANDLE_TIMEFRAMES.some((tf) => tf.value === interval)) {
          interval = findNearestTimeframe(interval);
        }
        if (!Number.isFinite(before) || before < 0) {
          setTypeFieldError('Alert before must be 0 or a positive number.');
          return;
        }
        if (before >= interval) {
          setTypeFieldError('Alert before must be less than candle interval.');
          return;
        }
        setTypeFieldError(null);
        rule = { ...base, id, enabled, order, type: 'candle-close-countdown', candleIntervalMinutes: interval, alertBeforeMinutes: before };
        break;
      }
      case 'fixed-interval': {
        const interval = parseInt(formIntervalMinutes, 10);
        if (!Number.isFinite(interval) || interval <= 0) {
          setTypeFieldError('Interval must be a positive number.');
          return;
        }
        setTypeFieldError(null);
        rule = { ...base, id, enabled, order, type: 'fixed-interval', intervalMinutes: interval };
        break;
      }
      case 'clock-mark': {
        const marks = parseMinuteMarks(formMinuteMarks);
        if (!marks || marks.length === 0) {
          setTypeFieldError('Enter at least one valid minute mark (0-59), comma-separated.');
          return;
        }
        setTypeFieldError(null);
        rule = { ...base, id, enabled, order, type: 'clock-mark', minuteMarks: marks };
        break;
      }
      case 'specific-time': {
        if (!isValidTimeFormat(formTime)) {
          setTypeFieldError('Enter a valid time in HH:MM format (00:00 - 23:59).');
          return;
        }
        setTypeFieldError(null);
        // Normalize to zero-padded HH:MM (guard validated above via isValidTimeFormat)
        const match = /^(\d{1,2}):(\d{2})$/.exec(formTime.trim());
        const hours = match?.[1] ?? '00';
        const minutes = match?.[2] ?? '00';
        const normalizedTime = `${hours.padStart(2, '0')}:${minutes}`;
        rule = { ...base, id, enabled, order, type: 'specific-time', time: normalizedTime };
        break;
      }
    }

    if (editingRuleId !== undefined) {
      setAlarmRules((prev) => prev.map((r) => (r.id === editingRuleId ? rule : r)));
    } else {
      setAlarmRules((prev) => [...prev, rule]);
    }

    closeFormSilent();
  }, [formType, formMessage, formColor, formSoundId, formTimezoneOverride, formCandleInterval, formAlertBefore, formIntervalMinutes, formMinuteMarks, formTime, editingRuleId, alarmRules, closeFormSilent]);

  // ---------------------------------------------------------------------------
  // Handlers — alarm rule list actions
  // ---------------------------------------------------------------------------

  const handleDeleteRule = useCallback((id: string): void => {
    setAlarmRules((prev) => prev.filter((r) => r.id !== id));
    setEditingRuleId((current) => {
      if (current === id) {
        setIsFormOpen(false);
        return undefined;
      }
      return current;
    });
  }, []);

  const handleToggleRule = useCallback((id: string, enabled: boolean): void => {
    setAlarmRules((prev) =>
      prev.map((r) => (r.id === id ? { ...r, enabled } : r)),
    );
  }, []);

  const handleMoveUp = useCallback((id: string): void => {
    setAlarmRules((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((r) => r.id === id);
      if (idx <= 0) return prev;
      const current = sorted[idx];
      const above = sorted[idx - 1];
      if (!current || !above) return prev;
      const currentOrder = current.order;
      const aboveOrder = above.order;
      return prev.map((r) => {
        if (r.id === current.id) return { ...r, order: aboveOrder };
        if (r.id === above.id) return { ...r, order: currentOrder };
        return r;
      });
    });
  }, []);

  const handleMoveDown = useCallback((id: string): void => {
    setAlarmRules((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((r) => r.id === id);
      if (idx < 0 || idx >= sorted.length - 1) return prev;
      const current = sorted[idx];
      const below = sorted[idx + 1];
      if (!current || !below) return prev;
      const currentOrder = current.order;
      const belowOrder = below.order;
      return prev.map((r) => {
        if (r.id === current.id) return { ...r, order: belowOrder };
        if (r.id === below.id) return { ...r, order: currentOrder };
        return r;
      });
    });
  }, []);

  const handlePreviewTone = useCallback((toneId: AlarmToneId): void => {
    void playAlarmTone(toneId);
  }, []);

  // ---------------------------------------------------------------------------
  // Handler — save modal
  // ---------------------------------------------------------------------------

  const handleSave = useCallback(async (): Promise<void> => {
    setSaveError(null);
    setIsSaving(true);
    try {
      await liveTradingSettingsService.updateAlarmConfig({
        alarmRules,
      });
      onClose();
    } catch {
      setSaveError('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [alarmRules, onClose]);

  // ---------------------------------------------------------------------------
  // Sorted rules for display
  // ---------------------------------------------------------------------------

  const sortedRules = [...alarmRules].sort((a, b) => a.order - b.order);

  // Whether the current candle interval form value is non-standard (backward-compat)
  const isCustomCandleInterval = !CANDLE_TIMEFRAMES.some((tf) => String(tf.value) === formCandleInterval);
  const nearestStandardInterval = isCustomCandleInterval ? findNearestTimeframe(parseInt(formCandleInterval, 10) || 60) : null;

  // ---------------------------------------------------------------------------
  // Alarm rule form JSX
  // ---------------------------------------------------------------------------

  const ruleForm = (
    <div className="flex flex-col gap-3">
      {/* Message */}
      <Input
        label="Message"
        labelPlacement='outside-left'
        size="sm"
        variant="bordered"
        isRequired
        value={formMessage}
        onValueChange={(v) => { setFormMessage(v); setMessageError(null); }}
        placeholder="e.g. Hourly candle closing soon"
        className="w-full"
        classNames={{ 
          base: 'w-full', 
          mainWrapper: "flex-1 w-full", 
          inputWrapper: 'w-full', 
          input: 'max-md:text-base!' , 
          label: 'pl-0 text-sm' 
        }}
        {...(messageError !== null ? { isInvalid: true, errorMessage: messageError } : {})}
      />
      {/* Rule type */}
      <Select
        label="Type"
        labelPlacement='outside-left'
        size="sm"
        variant="bordered"
        classNames={{
          label: 'text-sm' ,
          base: 'gap-8'
        }}
        selectedKeys={[formType]}
        onSelectionChange={(keys) => {
          if (keys === 'all') return;
          const key = Array.from(keys)[0] as AlarmRuleType | undefined;
          if (key) {
            setFormType(key);
            setTypeFieldError(null);
          }
        }}
      >
        {RULE_TYPE_OPTIONS.map((opt) => (
          <SelectItem key={opt.value} textValue={opt.label}>
            <div className="flex flex-col">
              <span className="text-sm">{opt.label}</span>
              <span className="text-xs text-default-400">{opt.description}</span>
            </div>
          </SelectItem>
        ))}
      </Select>


      {/* Type-specific fields */}
      {formType === 'candle-close-countdown' && (
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Select
              label="Candle Timeframe"
              size="sm"
              variant="bordered"
              selectedKeys={[formCandleInterval]}
              onSelectionChange={(keys) => {
                if (keys === 'all') return;
                const key = Array.from(keys)[0] as string | undefined;
                if (key) setFormCandleInterval(key);
              }}
              className="flex-1"
              description="Boundaries aligned to midnight"
            >
              {[
                ...CANDLE_TIMEFRAMES.map((tf) => (
                  <SelectItem key={String(tf.value)}>{tf.label}</SelectItem>
                )),
                ...(isCustomCandleInterval ? [
                  <SelectItem key={formCandleInterval}>Custom ({formCandleInterval} min)</SelectItem>,
                ] : []),
              ]}
            </Select>
            <Input
              label="Alert before (min)"
              size="sm"
              variant="bordered"
              type="number"
              value={formAlertBefore}
              onValueChange={setFormAlertBefore}
              placeholder="1"
              className="flex-1"
              classNames={{ input: 'max-md:text-base!' }}
            />
          </div>
          {isCustomCandleInterval && nearestStandardInterval !== null && (
            <p className="text-xs text-warning">
              Non-standard interval ({formCandleInterval} min) — will be saved as {nearestStandardInterval} min ({CANDLE_TIMEFRAMES.find((tf) => tf.value === nearestStandardInterval)?.label}).
            </p>
          )}
        </div>
      )}
      {formType === 'fixed-interval' && (
        <Input
          label="Interval (minutes)"
          size="sm"
          variant="bordered"
          type="number"
          value={formIntervalMinutes}
          onValueChange={setFormIntervalMinutes}
          placeholder="15"
          description="Fires at evenly-spaced intervals from midnight"
          classNames={{ input: 'max-md:text-base!' }}
        />
      )}
      {formType === 'clock-mark' && (
        <Input
          label="Minute marks"
          size="sm"
          variant="bordered"
          value={formMinuteMarks}
          onValueChange={setFormMinuteMarks}
          placeholder="0, 15, 30, 45"
          description="Comma-separated values (0-59)"
          classNames={{ input: 'max-md:text-base!' }}
        />
      )}
      {formType === 'specific-time' && (
        <Input
          label="Time (24h)"
          size="sm"
          variant="bordered"
          value={formTime}
          onValueChange={setFormTime}
          placeholder="09:30"
          description="HH:MM format (e.g., 09:30, 14:00)"
          classNames={{ input: 'max-md:text-base!' }}
        />
      )}

      {/* Type-specific validation error */}
      {typeFieldError !== null && (
        <p className="text-xs text-danger">{typeFieldError}</p>
      )}

      {/* Color selector */}
      <div className="flex flex-col gap-1.5">
        <p className="text-xs text-text-secondary font-medium">Toast Color</p>
        <div className="flex gap-1.5">
          {CATEGORY_COLORS.map((c) => (
            <Button
              key={c.cssVar}
              size="sm"
              isIconOnly
              variant={formColor === c.cssVar ? 'solid' : 'bordered'}
              color={c.cssVar as AlarmColor}
              onPress={() => setFormColor(c.cssVar as AlarmColor)}
              aria-label={c.name}
            />
          ))}
        </div>
      </div>

      {/* Sound selector + preview */}
      <div className="flex items-center gap-2">
        <Select
          label="Tone"
          size="sm"
          variant="bordered"
          selectedKeys={formSoundId !== null ? [formSoundId] : ['none']}
          onSelectionChange={(keys) => {
            if (keys === 'all') return;
            const key = Array.from(keys)[0] as string | undefined;
            if (key === 'none' || !key) {
              setFormSoundId(null);
            } else {
              setFormSoundId(key as AlarmToneId);
            }
          }}
          className="flex-1"
        >
          {[
            <SelectItem key="none">None (visual only)</SelectItem>,
            ...ALARM_TONE_IDS.map((id) => (
              <SelectItem key={id}>{ALARM_TONE_DISPLAY_MAP[id]}</SelectItem>
            )),
          ]}
        </Select>
        {formSoundId !== null && (
          <Button
            isIconOnly
            size="lg"
            variant="flat"
            color='primary'
            onPress={() => handlePreviewTone(formSoundId)}
            aria-label="Preview tone"
            // className="bg-primary-100"
          >
            <Volume2 size={18} />
          </Button>
        )}
      </div>

      {/* Timezone override */}
      <div className="flex flex-col gap-1">
        <Autocomplete
          label="Timezone override"
          labelPlacement="outside"
          placeholder="Search timezones..."
          size="sm"
          variant="bordered"
          selectedKey={formTimezoneOverride || null}
          isClearable
          menuTrigger="input"
          onSelectionChange={(key) => {
            setFormTimezoneOverride(key as string ?? '');
          }}
          aria-label="Alarm timezone override"
          className="max-md:[&_input]:text-base!"
        >
          {TIMEZONES.map((tz) => (
            <AutocompleteItem key={tz.value}>{tz.label}</AutocompleteItem>
          ))}
        </Autocomplete>
        {!formTimezoneOverride && (
          <span className="text-[10px] text-default-400">
            Using display timezone (default)
          </span>
        )}
      </div>

    </div>
  );

  // ---------------------------------------------------------------------------
  // Alarm rules list JSX
  // ---------------------------------------------------------------------------

  const rulesListContent = (
    <div className="flex flex-col gap-3">
      {sortedRules.length > 0 ? (
        <div className="flex flex-col gap-1.5">
            {sortedRules.map((rule, idx) => (
              <Card key={rule.id} shadow="sm" radius="sm">
                <CardHeader className="pb-2 pt-2 flex justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${ALARM_COLOR_BG_CLASS[rule.color]}`} />
                    {rule.message}
                  </div>
                  {/* Enable toggle */}
                  <Switch
                    size="sm"
                    color="primary"
                    isSelected={rule.enabled}
                    onValueChange={(enabled) => handleToggleRule(rule.id, enabled)}
                    aria-label={rule.enabled ? 'Disable alarm' : 'Enable alarm'}
                  />
                </CardHeader>
                <CardBody className="p-3 pt-0 pb-0">
                  <div className="flex items-center gap-2">
                    {/* Reorder buttons */}
                    <div className="flex flex-col gap-0.5 shrink-0 mr-3">
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        isDisabled={idx === 0}
                        onPress={() => handleMoveUp(rule.id)}
                        aria-label="Move up"
                        className='items-end'
                      >
                        <ChevronUp size={26} />
                      </Button>
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        isDisabled={idx === sortedRules.length - 1}
                        onPress={() => handleMoveDown(rule.id)}
                        aria-label="Move down"
                        className="items-start"
                      >
                        <ChevronDown size={26} />
                      </Button>
                    </div>

                    {/* Rule info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-text-secondary truncate pl-4">
                        {formatRuleSummary(rule)}
                      </p>
                    </div>

                    {/* Edit / Delete */}
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        onPress={() => openEditForm(rule)}
                        aria-label="Edit rule"
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        color="danger"
                        onPress={() => handleDeleteRule(rule.id)}
                        aria-label="Delete rule"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        ) : (
          <p className="text-xs text-text-tertiary text-center py-2">
            No alarm rules yet. Add one to enable time alerts.
          </p>
        )}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Modal close guard (respects dirty form on overlay click / ESC / X button)
  // ---------------------------------------------------------------------------

  const handleModalClose = useCallback((): void => {
    if (isFormOpen && isFormDirty) {
      pendingDiscardActionRef.current = () => {
        closeFormSilent();
        onClose();
      };
      discardDialog.onOpen();
      return;
    }
    if (isFormOpen) {
      closeFormSilent();
    }
    onClose();
  }, [isFormOpen, isFormDirty, onClose, closeFormSilent, discardDialog]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      placement="top-center"
      scrollBehavior="inside"
      size={isDesktop ? 'md' : 'full'}
      hideCloseButton
    >
      <ModalContent>
        {/* ---- HEADER ---- */}
        {isFormOpen ? (
          <ModalHeader className="flex flex-row items-center gap-2 pb-2 border-b-1 border-default-200">
            <Button isIconOnly size="sm" variant="light" onPress={closeForm} aria-label="Back to list">
              <ArrowLeft size={18} />
            </Button>
            <span className="text-base font-semibold">
              {editingRuleId !== undefined ? 'Edit Alarm Rule' : 'Add Alarm Rule'}
            </span>
          </ModalHeader>
        ) : (
          <ModalHeader className="flex flex-row items-center justify-between pb-2 border-b-1 border-default-200">
            <span className="text-base font-semibold">Alarms &amp; Reminders</span>
            <Button size="md" variant="light" color="primary" onPress={openAddForm}>
              + Add Rule
            </Button>
          </ModalHeader>
        )}

        {/* ---- BODY ---- */}
        {isFormOpen ? (
          <ModalBody className="gap-0 px-4 pb-2">
            {ruleForm}
          </ModalBody>
        ) : (
          <ModalBody className="gap-0 px-1 pb-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-text-secondary">Loading settings…</p>
              </div>
            ) : (
              rulesListContent
            )}
          </ModalBody>
        )}

        {/* ---- FOOTER ---- */}
        {isFormOpen ? (
          <ModalFooter className="flex flex-col gap-2 pt-3 border-t-1 border-default-200">
            {messageError !== null && (
              <p className="text-xs text-danger text-right w-full">{messageError}</p>
            )}
            {typeFieldError !== null && (
              <p className="text-xs text-danger text-right w-full">{typeFieldError}</p>
            )}
            <div className="flex items-center justify-between w-full">
              <Button variant="light" onPress={closeForm}>
                Cancel
              </Button>
              <Button
                color="primary"
                onPress={handleSaveRule}
              >
                {editingRuleId !== undefined ? 'Update Rule' : 'Add Rule'}
              </Button>
            </div>
          </ModalFooter>
        ) : (
          <ModalFooter className="flex flex-col gap-2 pt-3 border-t-1 border-default-200">
            {saveError !== null && (
              <p className="text-xs text-danger text-right w-full">{saveError}</p>
            )}
            <div className="flex items-center justify-between w-full">
              <Button variant="light" onPress={onClose} isDisabled={isSaving}>
                Cancel
              </Button>
              <Button
                color="primary"
                isLoading={isSaving}
                isDisabled={isSaving || isLoading}
                onPress={() => { void handleSave(); }}
              >
                Save
              </Button>
            </div>
          </ModalFooter>
        )}
        <UnsavedChangesDialog
          isOpen={discardDialog.isOpen}
          onClose={handleDiscardCancel}
          onDiscard={handleDiscard}
        />
      </ModalContent>
    </Modal>
  );
};
